// User-defined engines: a URL template plus modifiers that set query params.
// Stored as plain JSON so they can be exported, shared and imported.
import {
  type Category,
  type Engine,
  NeedsInputError,
  type Param,
} from './types';

export interface CustomModifier {
  /** Bang token, e.g. `cabin`. */
  token: string;
  label: string;
  /** Query params this modifier sets, e.g. { campsite_type: 'lodging' }. */
  set: Record<string, string>;
  /** Modifiers sharing a group are alternatives (only one at a time). */
  group?: string;
}

export interface CustomEngineDef {
  /** Bang without `!`; also the engine's identity. */
  bang: string;
  name: string;
  /** Search URL with `{q}` where the query goes. */
  template: string;
  description?: string;
  category?: Category;
  modifiers: CustomModifier[];
  /** Query params for a `$min-max` price range, if the site has one. */
  price?: { min?: string; max?: string };
  /**
   * Params that hold several filters in one value, with their separator:
   * Walmart's `facet=brand:X||fulfillment_method:Pickup` is `{ facet: '||' }`.
   * Modifiers setting such a param add to it instead of replacing it.
   */
  joins?: Record<string, string>;
}

/** Separators sites use to pack several filters into one param value. */
const JOINERS = ['||'];

/** Splits a packed filter value (`a:1||b:2`) into its parts, if it is one. */
export function splitJoined(
  value: string
): { parts: string[]; join: string } | undefined {
  for (const join of JOINERS) {
    const parts = value.split(join).filter(Boolean);
    if (parts.length > 1) return { parts, join };
  }
  return undefined;
}

/**
 * Params that two or more separate modifiers set, with no separator known:
 * used together, the last one wins.
 */
export function clashingParams(def: CustomEngineDef): string[] {
  const count = new Map<string, number>();
  for (const mod of def.modifiers) {
    if (mod.group) continue; // alternatives: only one applies anyway
    for (const k of Object.keys(mod.set)) count.set(k, (count.get(k) ?? 0) + 1);
  }
  return [...count]
    .filter(([k, n]) => n > 1 && !def.joins?.[k])
    .map(([k]) => k);
}

export const CUSTOM_PREFIX = 'custom:';
export const BANG_NAME = /^[a-z0-9_-]{1,24}$/;
export const TOKEN_NAME = /^[a-z0-9_+-][a-z0-9_+@-]{0,23}$/;

/**
 * A template is safe to navigate to: it parses as an http(s) URL and has a
 * `{q}`. Checked everywhere a template comes in (editor, import, storage) and
 * again before building, so a `javascript:` URL can never be opened.
 */
export function isWebTemplate(template: string): boolean {
  if (!template.includes('{q}')) return false;
  try {
    const url = new URL(template.replace(/\{q\}/g, 'x'));
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** Turns a stored definition into an engine the parser and builder can use. */
export function compileCustom(def: CustomEngineDef): Engine {
  const params: Param[] = [];
  // Ids come from slugs so presets keep working, but two tokens can slug
  // alike (a-b, a_b), so later ones get a numbered id instead of colliding.
  const taken = new Set<string>(['price']);
  const uniqueId = (base: string) => {
    let id = base;
    for (let n = 2; taken.has(id); n++) id = `${base}-${n}`;
    taken.add(id);
    return id;
  };
  const flagId = new Map<CustomModifier, string>();
  const groupId = new Map<string, string>();
  const groups = new Map<string, CustomModifier[]>();
  for (const mod of def.modifiers) {
    if (mod.group) {
      if (!groupId.has(mod.group))
        groupId.set(mod.group, uniqueId(`g-${slug(mod.group)}`));
      groups.set(mod.group, [...(groups.get(mod.group) ?? []), mod]);
    } else {
      const id = uniqueId(`m-${slug(mod.token)}`);
      flagId.set(mod, id);
      params.push({
        id,
        type: 'flag',
        token: mod.token,
        label: mod.label || mod.token,
        group: 'Modifiers',
      });
    }
  }
  for (const [group, mods] of groups) {
    params.push({
      id: groupId.get(group) ?? group,
      type: 'choice',
      label: group,
      group: 'Modifiers',
      options: mods.map(m => ({
        value: m.token,
        token: m.token,
        label: m.label || m.token,
      })),
    });
  }
  if (def.price?.min || def.price?.max) {
    params.push({
      id: 'price',
      type: 'range',
      prefix: '$',
      label: 'Price',
      group: 'Price',
    });
  }

  return {
    id: `${CUSTOM_PREFIX}${def.bang}`,
    name: def.name,
    bang: def.bang,
    aliases: [],
    homepage: safeOrigin(def.template),
    description: def.description ?? 'Your custom engine.',
    category: def.category ?? 'Custom',
    defaultOn: true,
    params,
    build({ query, values }) {
      if (!isWebTemplate(def.template))
        throw new NeedsInputError(
          `${def.name}’s URL isn’t a web address. Fix it on the Engines page.`,
          def.name
        );
      let url: URL;
      try {
        url = new URL(
          def.template.replace(/\{q\}/g, encodeURIComponent(query))
        );
      } catch {
        throw new NeedsInputError(
          `${def.name} can’t take these words in its URL.`,
          def.name
        );
      }
      const setHere = new Set<string>();
      for (const mod of def.modifiers) {
        const on = mod.group
          ? values[groupId.get(mod.group) ?? ''] === mod.token
          : values[flagId.get(mod) ?? ''] === true;
        if (!on) continue;
        for (const [k, v] of Object.entries(mod.set)) {
          const join = def.joins?.[k];
          const before = url.searchParams.get(k);
          url.searchParams.set(
            k,
            join && setHere.has(k) && before ? `${before}${join}${v}` : v
          );
          setHere.add(k);
        }
      }
      const price = values.price;
      if (typeof price === 'object') {
        if (def.price?.min && price.min !== undefined)
          url.searchParams.set(def.price.min, String(price.min));
        if (def.price?.max && price.max !== undefined)
          url.searchParams.set(def.price.max, String(price.max));
      }
      return url.toString();
    },
  };
}

function safeOrigin(template: string): string {
  try {
    return new URL(template.replace(/\{q\}/g, 'x')).origin;
  } catch {
    return '';
  }
}

export interface DetectedTemplate {
  template: string;
  /** Other params in the example URL, which can become modifiers. */
  params: Array<[key: string, value: string]>;
  /** Where the query was found, or null if it wasn't. */
  foundIn: 'param' | 'path' | null;
}

/**
 * Works out a template from a real results URL and the words searched for:
 * the param (or path segment) holding the query becomes `{q}`.
 */
// Query parameter names sites commonly use for the search words.
const QUERY_PARAMS = [
  'q',
  'query',
  'search_query',
  'searchterm',
  'search',
  'keywords',
  'keyword',
  'k',
  'kw',
  '_nkw',
  'term',
  'text',
  's',
  'p',
  'wd',
];

/** The search words in a results URL, guessed from common parameter names. */
export function guessQuery(example: string): string | undefined {
  try {
    // Names compared without case: some sites use searchTerm or Keywords.
    const params = new Map<string, string>();
    for (const [k, v] of new URL(example.trim()).searchParams)
      if (!params.has(k.toLowerCase())) params.set(k.toLowerCase(), v);
    for (const name of QUERY_PARAMS) {
      const v = params.get(name)?.trim();
      if (v) return v;
    }
  } catch {
    // not a URL
  }
  return undefined;
}

export function detectTemplate(
  example: string,
  query: string
): DetectedTemplate {
  const url = new URL(example.trim());
  const want = query.trim().toLowerCase();
  const params = [...url.searchParams.entries()];
  const hit = want
    ? params.find(([, v]) => v.trim().toLowerCase() === want)
    : undefined;
  if (hit) {
    url.searchParams.set(hit[0], '__Q__');
    return {
      template: url.toString().replace('__Q__', '{q}'),
      params: params.filter(([k]) => k !== hit[0]),
      foundIn: 'param',
    };
  }
  const segments = url.pathname.split('/');
  const i = want
    ? segments.findIndex(seg => {
        try {
          return (
            decodeURIComponent(seg.replace(/\+/g, ' ')).toLowerCase() === want
          );
        } catch {
          return false; // a malformed %xx segment can't be the query
        }
      })
    : -1;
  if (i >= 0) {
    segments[i] = '__Q__';
    url.pathname = segments.join('/');
    return {
      template: url.toString().replace('__Q__', '{q}'),
      params,
      foundIn: 'path',
    };
  }
  return { template: url.toString(), params, foundIn: null };
}

/** Removes a param from a template (when it becomes a modifier instead). */
export function dropParam(template: string, key: string): string {
  const url = new URL(template.replace(/\{q\}/g, '__Q__'));
  url.searchParams.delete(key);
  return url.toString().replace(/__Q__/g, '{q}');
}

/** Suggests a short token from a param value: "lodging (cabins/yurts)" -> "lodging". */
export function suggestToken(value: string): string {
  // Facet-style values name the filter after a colon:
  // `fulfillment_method:Delivery` -> "delivery".
  const named = value.includes(':')
    ? value.slice(value.lastIndexOf(':') + 1)
    : value;
  const word = named.toLowerCase().match(/[a-z0-9]+/)?.[0] ?? 'mod';
  return word.slice(0, 12);
}

const RESERVED_TOKENS = new Set(['x', 'exact', 'quote', 'plain']);

/**
 * Problems with a definition, as user-facing messages. `taken` says why a
 * bang is already used (by a built-in, another engine, a preset...), if it is.
 */
export function validateCustom(
  def: CustomEngineDef,
  taken: (bang: string) => string | undefined
): string[] {
  const errors: string[] = [];
  if (!def.name.trim()) errors.push('Give the engine a name.');
  if (!BANG_NAME.test(def.bang))
    errors.push('Bang: 1–24 characters, a–z, 0–9, - or _.');
  else {
    const why = taken(def.bang);
    if (why) errors.push(why);
  }
  if (!def.template.includes('{q}'))
    errors.push('The URL needs {q} where the search words go.');
  else if (!isWebTemplate(def.template))
    errors.push('The URL must be a web address starting with https://.');
  const seen = new Set<string>();
  for (const mod of def.modifiers) {
    if (!TOKEN_NAME.test(mod.token))
      errors.push(
        `Modifier "${mod.token}": a–z, 0–9, - or _, up to 24 characters.`
      );
    else if (RESERVED_TOKENS.has(mod.token) || mod.token.startsWith('$'))
      errors.push(`Modifier "${mod.token}" is reserved.`);
    else if (seen.has(mod.token))
      errors.push(`Modifier "${mod.token}" is used twice.`);
    seen.add(mod.token);
    if (Object.keys(mod.set).length === 0)
      errors.push(`Modifier "${mod.token}" doesn’t set any URL parameter.`);
  }
  return errors;
}

/** Parses "a=1&b=two" (as typed in the editor) into params. */
export function parseSet(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of new URLSearchParams(text.trim())) if (k) out[k] = v;
  return out;
}

export function formatSet(set: Record<string, string>): string {
  return Object.entries(set)
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
}
